/**
 * DepositWithdrawalManager - 出入金管理器
 * 
 * 处理存款、取款、交易商回馈、推荐人奖励等功能
 */

import type { 
  Transaction, 
  TransactionType, 
  TransactionStatus, 
  ClientDepositRecord 
} from '../types/user.types';
import { logger } from '../utils/logger';
import { storage } from '../utils/storage';
import { getDepositFeeDetails } from '../utils/feeCalculator';

export class DepositWithdrawalManager {
  private accountManager: any;
  private userManager: any;
  private tradingSettingsManager: any;
  private brokerSettingsManager: any;

  constructor(
    accountManager: any,
    userManager: any,
    tradingSettingsManager: any,
    brokerSettingsManager: any
  ) {
    this.accountManager = accountManager;
    this.userManager = userManager;
    this.tradingSettingsManager = tradingSettingsManager;
    this.brokerSettingsManager = brokerSettingsManager;
    
    logger.log('DepositWithdrawalManager initialized');
  }

  /**
   * 生成交易ID
   */
  private generateTransactionId(): string {
    return `TX${Date.now()}${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
  }

  /**
   * 生成记录ID
   */
  private generateRecordId(): string {
    return `REC${Date.now()}${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
  }

  /**
   * 存款
   */
  async deposit(
    uid: string,
    amount: number,
    paymentMethod: string
  ): Promise<{
    success: boolean;
    transactionId?: string;
    actualAmount?: number;
    fee?: number;
    errorMessage?: string;
  }> {
    try {
      // 1. 验证金额
      if (amount <= 0) {
        return { success: false, errorMessage: '存款金额必须大于0' };
      }
      
      // 2. 获取用户信息
      const user = this.userManager.getUser(uid);
      if (!user) {
        return { success: false, errorMessage: '用户不存在' };
      }
      
      const account = this.accountManager.getAccount(uid);
      const tradingSettings = this.tradingSettingsManager.getSettings(uid);
      const brokerUid = tradingSettings?.brokerUid || '10000001';
      
      // 3. 计算费用
      const brokerSettings = this.brokerSettingsManager.getSettings(brokerUid);
      const feeDetails = getDepositFeeDetails(user, brokerSettings);
      const fee = amount * feeDetails.actualRate;
      const actualDepositAmount = amount - fee;
      
      // 4. 更新账户余额（先记录存款）
      const balanceBeforeDeposit = account.balance;
      account.balance += actualDepositAmount;
      account.availableBalance += actualDepositAmount;
      account.updatedAt = Date.now();
      this.accountManager.accounts.set(uid, account);
      this.accountManager.saveAccount(account);
      
      // 5. 记录存款交易
      const depositTxId = this.accountManager.recordTransaction({
        transactionId: this.generateTransactionId(),
        uid,
        type: 'deposit' as TransactionType,
        amount: actualDepositAmount,
        balanceBefore: balanceBeforeDeposit,
        balanceAfter: account.balance,
        status: 'completed' as TransactionStatus,
        metadata: {
          paymentMethod,
          notes: `存款 ${amount.toFixed(2)} USDT`
        },
        createdAt: Date.now()
      });
      
      // 6. 记录手续费交易
      if (fee > 0) {
        this.accountManager.recordTransaction({
          transactionId: this.generateTransactionId(),
          uid,
          type: 'deposit_fee' as TransactionType,
          amount: -fee,
          fee: fee,
          balanceBefore: balanceBeforeDeposit + amount,
          balanceAfter: account.balance,
          status: 'completed' as TransactionStatus,
          metadata: {
            notes: `存款手续费 ${fee.toFixed(2)} USDT (${(feeDetails.actualRate * 100).toFixed(2)}%)`
          },
          createdAt: Date.now()
        });
      }
      
      // 7. 处理交易商回馈
      await this.processBrokerRebate(brokerUid, uid, amount, fee);
      
      // 8. 处理推荐人奖励
      if (user.profile?.referrerCode) {
        await this.processReferrerReward(
          user.profile.referrerCode,
          uid,
          fee
        );
      }
      
      // 9. 通知交易商
      await this.notifyBrokerDeposit(brokerUid, uid, amount, fee, actualDepositAmount);
      
      logger.log(`✅ Deposit successful: ${uid} deposited ${amount} USDT, actual: ${actualDepositAmount.toFixed(2)} USDT`);
      
      return {
        success: true,
        transactionId: depositTxId,
        actualAmount: actualDepositAmount,
        fee
      };
    } catch (error) {
      logger.error('Deposit error:', error);
      return {
        success: false,
        errorMessage: error instanceof Error ? error.message : '存款失败'
      };
    }
  }

  /**
   * 取款
   */
  async withdraw(
    uid: string,
    amount: number,
    walletAddress: string
  ): Promise<{
    success: boolean;
    transactionId?: string;
    errorMessage?: string;
  }> {
    try {
      // 1. 验证
      const validation = this.canWithdraw(uid, amount);
      if (!validation.canWithdraw) {
        return { success: false, errorMessage: validation.reason };
      }
      
      // 2. 获取账户
      const account = this.accountManager.getAccount(uid);
      const balanceBefore = account.balance;
      
      // 3. 扣除余额
      account.balance -= amount;
      account.availableBalance -= amount;
      account.updatedAt = Date.now();
      this.accountManager.accounts.set(uid, account);
      this.accountManager.saveAccount(account);
      
      // 4. 记录交易
      const txId = this.accountManager.recordTransaction({
        transactionId: this.generateTransactionId(),
        uid,
        type: 'withdrawal' as TransactionType,
        amount: -amount,
        balanceBefore: balanceBefore,
        balanceAfter: account.balance,
        status: 'completed' as TransactionStatus,
        metadata: {
          walletAddress,
          notes: `取款 ${amount.toFixed(2)} USDT 到 ${walletAddress}`
        },
        createdAt: Date.now()
      });
      
      logger.log(`✅ Withdrawal successful: ${uid} withdrew ${amount} USDT`);
      
      return {
        success: true,
        transactionId: txId
      };
    } catch (error) {
      logger.error('Withdrawal error:', error);
      return {
        success: false,
        errorMessage: error instanceof Error ? error.message : '取款失败'
      };
    }
  }

  /**
   * 验证是否可以取款
   */
  canWithdraw(uid: string, amount: number): {
    canWithdraw: boolean;
    reason?: string;
  } {
    if (amount <= 0) {
      return { canWithdraw: false, reason: '取款金额必须大于0' };
    }
    
    const account = this.accountManager.getAccount(uid);
    
    if (account.availableBalance < amount) {
      return {
        canWithdraw: false,
        reason: `可用余额不足。可用：${account.availableBalance.toFixed(2)} USDT`
      };
    }
    
    return { canWithdraw: true };
  }

  /**
   * 处理交易商回馈（自动转账）
   */
  async processBrokerRebate(
    brokerUid: string,
    clientUid: string,
    depositAmount: number,
    _depositFee: number  // 保留参数用于接口一致性
  ): Promise<void> {
    try {
      // 1. 获取交易商回馈设置
      const brokerSettings = this.brokerSettingsManager.getSettings(brokerUid);
      
      if (!brokerSettings?.rebate.enabled) {
        logger.log(`⏭️ Broker rebate disabled for ${brokerUid}`);
        return;
      }
      
      // 2. 计算应回馈金额
      const rebateRate = brokerSettings.rebate.rate;
      const fullRebateAmount = depositAmount * rebateRate;
      
      // 3. 检查限制
      const restrictions = brokerSettings.rebate.restrictions;
      const timeWindowMs = restrictions.timeWindowHours * 60 * 60 * 1000;
      const windowStart = Date.now() - timeWindowMs;
      
      // 获取时间窗口内的已回馈总额
      const recentRebates = this.accountManager.getTransactionHistory(brokerUid, {
        type: 'broker_rebate_out' as TransactionType,
        startTime: windowStart
      });
      
      const usedAmount = recentRebates.reduce((sum: number, tx: Transaction) => sum + Math.abs(tx.amount), 0);
      const remainingAmount = restrictions.maxAmountPerWindow - usedAmount;
      
      // 4. 确定实际回馈金额
      let actualRebateAmount = fullRebateAmount;
      let rebateStatus: 'completed' | 'partial' | 'limit_reached' = 'completed';
      
      if (remainingAmount <= 0) {
        actualRebateAmount = 0;
        rebateStatus = 'limit_reached';
        logger.warn(`⚠️ Broker rebate limit reached for ${brokerUid} (${restrictions.timeWindowHours}h window)`);
      } else if (fullRebateAmount > remainingAmount) {
        actualRebateAmount = remainingAmount;
        rebateStatus = 'partial';
        logger.warn(`⚠️ Partial broker rebate for ${brokerUid}: ${actualRebateAmount.toFixed(2)} / ${fullRebateAmount.toFixed(2)} USDT`);
      }
      
      if (actualRebateAmount <= 0) {
        return;
      }
      
      // 5. 从交易商账户扣除
      const brokerAccount = this.accountManager.getAccount(brokerUid);
      
      if (brokerAccount.availableBalance < actualRebateAmount) {
        logger.error(`❌ Broker ${brokerUid} insufficient balance for rebate: ${brokerAccount.availableBalance} < ${actualRebateAmount}`);
        return;
      }
      
      const brokerBalanceBefore = brokerAccount.balance;
      brokerAccount.balance -= actualRebateAmount;
      brokerAccount.availableBalance -= actualRebateAmount;
      brokerAccount.updatedAt = Date.now();
      this.accountManager.accounts.set(brokerUid, brokerAccount);
      this.accountManager.saveAccount(brokerAccount);
      
      // 6. 记录交易商支出
      this.accountManager.recordTransaction({
        transactionId: this.generateTransactionId(),
        uid: brokerUid,
        type: 'broker_rebate_out' as TransactionType,
        amount: -actualRebateAmount,
        balanceBefore: brokerBalanceBefore,
        balanceAfter: brokerAccount.balance,
        status: 'completed' as TransactionStatus,
        metadata: {
          clientUid,
          rebateRate,
          rebateWindowHours: restrictions.timeWindowHours,
          rebateMaxAmount: restrictions.maxAmountPerWindow,
          rebateUsedAmount: usedAmount + actualRebateAmount,
          notes: `回馈给客户 ${clientUid}，存款 ${depositAmount.toFixed(2)} USDT`
        },
        createdAt: Date.now()
      });
      
      // 7. 转入客户账户
      const clientAccount = this.accountManager.getAccount(clientUid);
      const clientBalanceBefore = clientAccount.balance;
      clientAccount.balance += actualRebateAmount;
      clientAccount.availableBalance += actualRebateAmount;
      clientAccount.updatedAt = Date.now();
      this.accountManager.accounts.set(clientUid, clientAccount);
      this.accountManager.saveAccount(clientAccount);
      
      // 8. 记录客户收入
      this.accountManager.recordTransaction({
        transactionId: this.generateTransactionId(),
        uid: clientUid,
        type: 'broker_rebate_in' as TransactionType,
        amount: actualRebateAmount,
        balanceBefore: clientBalanceBefore,
        balanceAfter: clientAccount.balance,
        status: 'completed' as TransactionStatus,
        metadata: {
          brokerUid,
          rebateRate,
          notes: `交易商回馈，存款 ${depositAmount.toFixed(2)} USDT`
        },
        createdAt: Date.now()
      });
      
      logger.log(`✅ Broker rebate: ${brokerUid} -> ${clientUid}: ${actualRebateAmount.toFixed(2)} USDT (${rebateStatus})`);
    } catch (error) {
      logger.error('Broker rebate error:', error);
    }
  }

  /**
   * 处理推荐人奖励
   */
  async processReferrerReward(
    referrerUid: string,
    referredUid: string,
    depositFee: number
  ): Promise<void> {
    try {
      // 1. 计算推荐人奖励（平台费用的 10%）
      // 注意：平台收取全额存款费用，与交易商回馈无关
      const rewardRate = 0.1;  // 10%
      const rewardAmount = depositFee * rewardRate;
      
      if (rewardAmount <= 0) {
        logger.warn(`⚠️ No referrer reward: deposit fee = ${depositFee}`);
        return;
      }
      
      // 2. 添加到推荐人账户
      const referrerAccount = this.accountManager.getAccount(referrerUid);
      const balanceBefore = referrerAccount.balance;
      referrerAccount.balance += rewardAmount;
      referrerAccount.availableBalance += rewardAmount;
      referrerAccount.updatedAt = Date.now();
      this.accountManager.accounts.set(referrerUid, referrerAccount);
      this.accountManager.saveAccount(referrerAccount);
      
      // 3. 记录交易
      this.accountManager.recordTransaction({
        transactionId: this.generateTransactionId(),
        uid: referrerUid,
        type: 'referrer_reward' as TransactionType,
        amount: rewardAmount,
        balanceBefore: balanceBefore,
        balanceAfter: referrerAccount.balance,
        status: 'completed' as TransactionStatus,
        metadata: {
          referredUid,
          rewardRate,
          depositFee,
          notes: `推荐人奖励，来自用户 ${referredUid} 的存款（平台费用 ${depositFee.toFixed(2)} USDT × 10%）`
        },
        createdAt: Date.now()
      });
      
      logger.log(`✅ Referrer reward: ${referrerUid} received ${rewardAmount.toFixed(2)} USDT from ${referredUid} (${depositFee.toFixed(2)} × 10%)`);
    } catch (error) {
      logger.error('Referrer reward error:', error);
    }
  }

  /**
   * 通知交易商客户存款
   */
  async notifyBrokerDeposit(
    brokerUid: string,
    clientUid: string,
    depositAmount: number,
    depositFee: number,
    actualAmount: number
  ): Promise<void> {
    try {
      const clientUser = this.userManager.getUser(clientUid);
      
      // 获取回馈金额（从最近的交易记录中）
      const recentTransactions = this.accountManager.getTransactionHistory(clientUid, {
        limit: 5
      });
      
      const rebateTx = recentTransactions.find(
        (tx: Transaction) => tx.type === 'broker_rebate_in'
      );
      
      const rebateAmount = rebateTx ? rebateTx.amount : 0;
      const rebateStatus = rebateAmount > 0 ? 'completed' : 'pending';
      
      const record: ClientDepositRecord = {
        recordId: this.generateRecordId(),
        brokerUid,
        clientUid,
        clientUsername: clientUser?.username || 'Unknown',
        depositAmount,
        depositFee: depositFee,  // 明确使用
        actualAmount,
        rebateAmount,
        rebateStatus: rebateStatus as any,
        depositTime: Date.now(),
        rebateTime: rebateAmount > 0 ? Date.now() : undefined,
        isRead: false,
        createdAt: Date.now()
      };
      
      // 保存到 localStorage
      const key = `broker_deposit_records_${brokerUid}`;
      const records = storage.get<ClientDepositRecord[]>(key, []);
      records.unshift(record);  // 最新的在前
      
      // 限制最多保存 500 条
      if (records.length > 500) {
        records.splice(500);
      }
      
      storage.set(key, records);
      
      logger.log(`📬 Notified broker ${brokerUid} of client ${clientUid} deposit`);
    } catch (error) {
      logger.error('Notify broker deposit error:', error);
    }
  }

  /**
   * 获取客户存款记录（交易商视角）
   */
  getClientDepositRecords(brokerUid: string, limit: number = 50): ClientDepositRecord[] {
    const key = `broker_deposit_records_${brokerUid}`;
    const records = storage.get<ClientDepositRecord[]>(key, []);
    return records.slice(0, limit);
  }

  /**
   * 标记客户存款记录为已读
   */
  markDepositRecordAsRead(brokerUid: string, recordId: string): void {
    const key = `broker_deposit_records_${brokerUid}`;
    const records = storage.get<ClientDepositRecord[]>(key, []);
    
    const record = records.find(r => r.recordId === recordId);
    if (record) {
      record.isRead = true;
      storage.set(key, records);
    }
  }
}

